import { logDebugSprintf, logErrorSprintf, sprintf } from '../utils/utils';
import { EAI_SCORE_CONTEXT_KEY, EMOTION_AI_EVENT_URL, EMOTION_AI_UC_URL, FETCH_EAI_SCORE, FETCH_EAI_SCORE_ERROR, FETCH_EAI_SCORE_SUCCESS, HEADER_APPLICATION_JSON, HEADER_CONTENT_TYPE, MAX_COLLECTING_TIME_MS, MAX_LAST_COLLECTING_TIME_MS, MAX_SCORING_POLLING_TIME, SCORING_INTERVAL, SEND_EAI_EVENT, SEND_EAI_EVENT_ERROR, SEND_EAI_EVENT_SUCCESS } from '../enum/FlagshipConstant';
import { TroubleshootingLabel } from '../types';
import { LogLevel } from '../enum/index';
import { UsageHit } from '../hit/UsageHit';
import { Troubleshooting } from '../hit/Troubleshooting.ts';
export class CommonEmotionAI {
    _EAIScore;
    _EAIScoreChecked;
    _httpClient;
    _sdkConfig;
    _eAIConfig;
    _isEAIDataCollecting;
    _fetchEAIScorePromise;
    _startScoringTimestamp;
    _scoringIntervalId;
    _onEAICollectStatusChange;
    _visitor;
    /**
     * Indicates whether EAI data has been collected
     */
    _startCollectingEAIDataTimestamp;
    constructor({ httpClient, sdkConfig, eAIConfig }) {
        this._EAIScoreChecked = false;
        this._httpClient = httpClient;
        this._sdkConfig = sdkConfig;
        this._eAIConfig = eAIConfig;
        this._isEAIDataCollecting = false;
    }
    init(visitor) {
        this._visitor = visitor;
    }
    onEAICollectStatusChange(callback) {
        this._onEAICollectStatusChange = callback;
    }
    get EAIScore() {
        return this._EAIScore;
    }
    get EAIScoreChecked() {
        return this._EAIScoreChecked;
    }
    setIsEAIDataCollected(isCollected) {
        return this._visitor.setIsEAIDataCollected(isCollected);
    }
    isEAIDataCollected() {
        return this._visitor.isEAIDataCollected();
    }
    sendEAIScoreTroubleshooting(eAIScore, endpoint) {
        const troubleshooting = new Troubleshooting({
            flagshipInstanceId: this._visitor.sdkInitialData?.instanceId,
            visitorId: this._visitor.visitorId,
            label: TroubleshootingLabel.EMOTION_AI_SCORE_FROM_LOCAL_CACHE,
            logLevel: LogLevel.DEBUG,
            eAIScore,
            isEAIScoreFromLocalCache: true,
            config: this._sdkConfig,
            httpRequestUrl: endpoint,
            traffic: this._visitor.traffic
        });
        this._visitor.sendTroubleshooting(troubleshooting);
    }
    // eslint-disable-next-line max-params
    sendRequestTroubleshooting(response, label, endpoint, method = 'GET', apiKeys, eAIScore) {
        const troubleshooting = new Troubleshooting({
            flagshipInstanceId: this._visitor.sdkInitialData?.instanceId,
            visitorId: this._visitor.visitorId,
            label,
            logLevel: LogLevel.DEBUG,
            hitContent: apiKeys,
            eAIScore,
            httpResponseBody: response.body,
            httpRequestMethod: method,
            httpRequestUrl: endpoint,
            httpResponseCode: response.status,
            httpResponseHeaders: response.headers,
            traffic: this._visitor.traffic,
            config: this._sdkConfig
        });
        this._visitor.sendTroubleshooting(troubleshooting);
    }
    sendRequestTroubleshootingError(error, label, endpoint, apiKeys) {
        const troubleshooting = new Troubleshooting({
            flagshipInstanceId: this._visitor.sdkInitialData?.instanceId,
            visitorId: this._visitor.visitorId,
            label,
            logLevel: LogLevel.ERROR,
            httpRequestMethod: 'GET',
            httpRequestUrl: endpoint,
            hitContent: apiKeys,
            traffic: this._visitor.traffic,
            httpResponseBody: error?.message,
            httpResponseHeaders: error?.headers,
            httpResponseCode: error?.statusCode,
            config: this._sdkConfig
        });
        this._visitor.sendTroubleshooting(troubleshooting);
    }
    sendCollectingTroubleshooting(timestamp, label, score) {
        const troubleshooting = new Troubleshooting({
            flagshipInstanceId: this._visitor.sdkInitialData?.instanceId,
            visitorId: this._visitor.visitorId,
            label,
            logLevel: LogLevel.DEBUG,
            eAIDataTimestamp: new Date(timestamp).toISOString(),
            config: this._sdkConfig,
            eAIScore: score,
            traffic: this._visitor.traffic
        });
        this._visitor.sendTroubleshooting(troubleshooting);
    }
    sendCollectingUsageHit(label) {
        const usageHit = new UsageHit({
            visitorId: this._visitor.sdkInitialData?.instanceId,
            label,
            config: this._sdkConfig,
            logLevel: LogLevel.DEBUG
        });
        this._visitor.sendUsageHit(usageHit);
    }
    async fetchEAIScore(noCache = false) {
        if (!this._eAIConfig?.eaiActivationEnabled) {
            return undefined;
        }
        if (this._EAIScoreChecked) {
            return this._EAIScore;
        }
        const cachedEAIScore = await this._visitor.getCachedEAIScore();
        if (cachedEAIScore) {
            this._EAIScore = cachedEAIScore;
            this._EAIScoreChecked = true;
            this._visitor.context[EAI_SCORE_CONTEXT_KEY] = this._EAIScore?.eai?.eas;
            this.sendEAIScoreTroubleshooting(this._EAIScore);
            return this._EAIScore;
        }
        if (this._fetchEAIScorePromise) {
            await this._fetchEAIScorePromise;
            return this._EAIScore;
        }
        const visitorId = this._visitor.visitorId;
        const url = sprintf(EMOTION_AI_UC_URL, this._sdkConfig.envId, visitorId);
        const versionQuery = noCache ? `&v=${Date.now()}` : '';
        const endpoint = `${url}?partner=eai${versionQuery}`;
        try {
            this._fetchEAIScorePromise = this._httpClient.getAsync(endpoint);
            const response = await this._fetchEAIScorePromise;
            this._EAIScore = response.body;
            this._EAIScoreChecked = true;
            this.sendRequestTroubleshooting(response, TroubleshootingLabel.EMOTION_AI_SCORE, endpoint, 'GET', undefined, this._EAIScore);
            if (this._EAIScore) {
                this._visitor.setCachedEAIScore(this._EAIScore);
                this._visitor.updateContext(EAI_SCORE_CONTEXT_KEY, this._EAIScore?.eai?.eas);
            }
            logDebugSprintf(this._sdkConfig, FETCH_EAI_SCORE, FETCH_EAI_SCORE_SUCCESS, visitorId, this._EAIScore);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (error) {
            logErrorSprintf(this._sdkConfig, FETCH_EAI_SCORE, FETCH_EAI_SCORE_ERROR, visitorId, error.message);
            this.sendRequestTroubleshootingError(error, TroubleshootingLabel.EMOTION_AI_SCORE_ERROR, endpoint);
        }
        finally {
            this._fetchEAIScorePromise = undefined;
        }
        return this._EAIScore;
    }
    async collectEAIEventsAsync(currentPage) {
        if (this._isEAIDataCollecting ||
            !this._eAIConfig?.eaiCollectEnabled) {
            return;
        }
        const isEAIDataCollected = await this.isEAIDataCollected();
        if (!this._eAIConfig.eaiActivationEnabled && isEAIDataCollected) {
            return;
        }
        const score = await this.fetchEAIScore();
        if (score) {
            return;
        }
        const visitorId = this._visitor.visitorId;
        await this.startCollectingEAIData(visitorId, currentPage);
    }
    async reportPageView(pageView) {
        if (!this._isEAIDataCollecting) {
            return;
        }
        return this.sendEAIEvent(pageView);
    }
    async sendEAIEvent(event) {
        const apiKeys = event.toApiKeys();
        try {
            const response = await this._httpClient.postAsync(EMOTION_AI_EVENT_URL, {
                body: apiKeys,
                headers: { [HEADER_CONTENT_TYPE]: HEADER_APPLICATION_JSON }
            });
            const label = apiKeys.t === 'PAGEVIEW' ? TroubleshootingLabel.EMOTION_AI_PAGE_VIEW : TroubleshootingLabel.EMOTION_AI_VISITOR_EVENT;
            this.sendRequestTroubleshooting(response, label, EMOTION_AI_EVENT_URL, 'POST', event.toApiKeys());
            logDebugSprintf(this._sdkConfig, SEND_EAI_EVENT, SEND_EAI_EVENT_SUCCESS, event.toApiKeys());
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (error) {
            const label = apiKeys.t === 'PAGEVIEW' ? TroubleshootingLabel.EMOTION_AI_PAGE_VIEW_ERROR : TroubleshootingLabel.EMOTION_AI_VISITOR_EVENT_ERROR;
            this.sendRequestTroubleshootingError(error, label, EMOTION_AI_EVENT_URL, apiKeys);
            logErrorSprintf(this._sdkConfig, SEND_EAI_EVENT, SEND_EAI_EVENT_ERROR, error.message);
        }
    }
    finalizeDataCollection(isCollected) {
        if (this._scoringIntervalId) {
            clearInterval(this._scoringIntervalId);
            this._scoringIntervalId = undefined;
        }
        this._isEAIDataCollecting = false;
        this.setIsEAIDataCollected(isCollected);
    }
    stopCollectingEAIData() {
        this.sendCollectingTroubleshooting(Date.now(), TroubleshootingLabel.EMOTION_AI_STOP_COLLECTING);
        this.sendCollectingUsageHit(TroubleshootingLabel.EMOTION_AI_STOP_COLLECTING);
        this.removeListeners();
        if (!this._eAIConfig?.eaiActivationEnabled) {
            this._isEAIDataCollecting = false;
            this.setIsEAIDataCollected(true);
            return;
        }
        this._startScoringTimestamp = Date.now();
        this.sendCollectingTroubleshooting(this._startScoringTimestamp, TroubleshootingLabel.EMOTION_AI_START_SCORING);
        this._scoringIntervalId = setInterval(async () => {
            const elapsedTime = Date.now() - this._startScoringTimestamp;
            if (elapsedTime > MAX_SCORING_POLLING_TIME) {
                this.sendCollectingTroubleshooting(Date.now(), TroubleshootingLabel.EMOTION_AI_SCORING_FAILED);
                this.finalizeDataCollection(false);
                return;
            }
            this._EAIScoreChecked = false;
            const score = await this.fetchEAIScore(true);
            if (score) {
                this.sendCollectingTroubleshooting(Date.now(), TroubleshootingLabel.EMOTION_AI_SCORING_SUCCESS, score);
                this.finalizeDataCollection(true);
            }
        }, SCORING_INTERVAL);
    }
    async reportVisitorEvent(visitorEvent) {
        if (!this._isEAIDataCollecting) {
            return;
        }
        const timestampDiff = Date.now() - this._startCollectingEAIDataTimestamp;
        if (timestampDiff <= MAX_COLLECTING_TIME_MS) {
            this.sendEAIEvent(visitorEvent);
        }
        else if ((timestampDiff <= MAX_LAST_COLLECTING_TIME_MS)) {
            this.sendEAIEvent(visitorEvent);
            this.stopCollectingEAIData();
        }
        else {
            this.removeListeners();
            this._isEAIDataCollecting = false;
            this.setIsEAIDataCollected(false);
        }
    }
}
